/**
 * No job left open — the iPad's watcher (threads/no-job-left-open/).
 *
 * Lives in the app shell, so it runs on EVERY screen, not just the job screen
 * (the old watch only ran while the job screen was open — that's how Arlington
 * HS logged nothing on 10/5). It opens the question when:
 *   · GPS sees him leave a job that's still open (any screen);
 *   · he opens the app or comes back to it and he's off site (or GPS is off);
 *   · he taps On my way to another job (guardOnMyWay — the block screen,
 *     left-open-block.tsx: the three answers + Call the office right on it);
 *   · a push deep-links in (?leaveAnswer=1, the 5:00 PM and overnight pings).
 * "I'm still here" is saved on the iPad, so changing screens doesn't reset it.
 *
 * Inert unless the server says the no_job_left_open switch is on for him.
 * Helpers are never asked.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { queueOfflineAction, useOfflineSync } from "@/lib/offline-sync";
import {
    LEFT_OPEN_KEY,
    STILL_HERE_NO_GPS_MS,
    STILL_HERE_ON_SITE_MS,
    answeredOffline,
    blockingJob,
    clearStillHere,
    haversineMeters,
    isStillHere,
    markAnsweredOffline,
    setStillHere,
    testedCount,
    useLeftOpenJobs,
    type LeaveAnswerBody,
    type LeftOpenJob,
} from "@/lib/left-open";
import { LeftOpenPrompt, type LeftOpenPromptProps } from "./left-open-prompt";
import { LeftOpenBlock } from "./left-open-block";
import {
    PartsDispositionDialog,
    type GatePart,
    type PartDispositionAnswer,
} from "@/pages/field/job-detail/parts-custody-card";

type Trigger = LeftOpenPromptProps["trigger"];

interface LeftOpenContextValue {
    /** The switch is on for this tech (the server said so). */
    on: boolean;
    jobs: LeftOpenJob[];
    /** Jobs still waiting for his answer (drives the Today card). */
    waiting: LeftOpenJob[];
    openPrompt: (jobId: string, trigger: Trigger, screen?: "coming_back" | "cant_finish") => boolean;
    /** True when On my way to `targetJobId` must wait — the block screen is now open. */
    guardOnMyWay: (targetJobId: string, targetLabel?: string | null) => boolean;
}

const OFF: LeftOpenContextValue = {
    on: false,
    jobs: [],
    waiting: [],
    openPrompt: () => false,
    guardOnMyWay: () => false,
};

const LeftOpenContext = createContext<LeftOpenContextValue>(OFF);

export function useLeftOpen(): LeftOpenContextValue {
    return useContext(LeftOpenContext);
}

const DEFAULT_RADIUS_M = 150;
const DEFAULT_GRACE_S = 60;

export function LeftOpenProvider({ children }: { children: React.ReactNode }) {
    const { user, isField } = useAuth();
    const isHelper = (user as { is_field_helper?: boolean } | null)?.is_field_helper === true;
    const enabled = isField && !isHelper && !!user;
    const query = useLeftOpenJobs(enabled);
    const on = enabled && query.data?.switchOn === true;
    // One component either way: swapping the tree under {children} when the
    // switch read lands would remount the whole app.
    return (
        <LeftOpenEngine
            on={on}
            jobs={on ? (query.data?.jobs ?? EMPTY) : EMPTY}
            officePhone={on ? (query.data?.officePhone ?? null) : null}
        >
            {children}
        </LeftOpenEngine>
    );
}

const EMPTY: LeftOpenJob[] = [];

function LeftOpenEngine({
    on,
    jobs,
    officePhone,
    children,
}: {
    on: boolean;
    jobs: LeftOpenJob[];
    officePhone: string | null;
    children: React.ReactNode;
}) {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation();
    const { isOnline } = useOfflineSync();
    const [prompt, setPrompt] = useState<{
        jobId: string;
        trigger: Trigger;
        screen?: "coming_back" | "cant_finish";
    } | null>(null);
    // The blocked On my way screen: which open job blocks, and where he was headed.
    const [block, setBlock] = useState<{ jobId: string; nextLabel: string | null } | null>(null);
    const [partsGate, setPartsGate] = useState<{ parts: GatePart[]; body: Omit<LeaveAnswerBody, "tappedAt"> } | null>(
        null,
    );
    const [, forceTick] = useState(0);

    const geofence = useQuery({
        queryKey: ["settings", "geofence"],
        queryFn: () => api.get<{ radiusM: number; graceSeconds: number }>("/api/settings/geofence"),
        staleTime: 10 * 60_000,
        retry: 1,
        enabled: on,
    });
    const radiusM = geofence.data?.radiusM ?? DEFAULT_RADIUS_M;
    const graceS = geofence.data?.graceSeconds ?? DEFAULT_GRACE_S;

    // An answer queued offline counts as answered until the server catches up.
    const [answeredRev, setAnsweredRev] = useState(0);
    const live = useMemo(
        () => jobs.filter((j) => !(j.state === "ask" && answeredOffline(j.jobId))),
        // answeredRev: re-read the iPad's offline answers after one is queued.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [jobs, answeredRev],
    );
    const waiting = useMemo(() => live.filter((j) => j.state === "ask"), [live]);
    const byId = useMemo(() => new Map(live.map((j) => [j.jobId, j])), [live]);

    // ── GPS: one watch for all his open jobs ─────────────────────────────
    const [gps, setGps] = useState<"unknown" | "ok" | "off">("unknown");
    const distanceRef = useRef(new Map<string, number>());
    const outsideSinceRef = useRef(new Map<string, number>());
    const pingedRef = useRef(new Set<string>());
    const resumeCheckRef = useRef(true); // the app just opened
    const promptRef = useRef(prompt);
    promptRef.current = prompt;
    const liveRef = useRef(live);
    liveRef.current = live;

    const open = useCallback((jobId: string, trigger: Trigger, screen?: "coming_back" | "cant_finish"): boolean => {
        if (!liveRef.current.some((j) => j.jobId === jobId)) return false;
        if (promptRef.current) return true;
        setPrompt({ jobId, trigger, screen });
        return true;
    }, []);

    const watched = useMemo(
        () =>
            live.filter(
                (j) => (j.state === "ask" || j.state === "later_today") && j.siteLat != null && j.siteLng != null,
            ),
        [live],
    );
    const watchKey = watched.map((j) => j.jobId).join(",");

    useEffect(() => {
        if (!on) return;
        if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
            setGps("off");
            return;
        }
        if (watched.length === 0) return;
        const id = navigator.geolocation.watchPosition(
            (pos) => {
                setGps("ok");
                const now = Date.now();
                const resume = resumeCheckRef.current;
                resumeCheckRef.current = false;
                for (const j of liveRef.current) {
                    if (j.siteLat == null || j.siteLng == null) continue;
                    const d = haversineMeters(pos.coords.latitude, pos.coords.longitude, j.siteLat, j.siteLng);
                    distanceRef.current.set(j.jobId, d);
                    if (d <= radiusM) {
                        outsideSinceRef.current.delete(j.jobId);
                        continue;
                    }
                    if (!outsideSinceRef.current.has(j.jobId)) outsideSinceRef.current.set(j.jobId, now);
                    const outsideMs = now - (outsideSinceRef.current.get(j.jobId) ?? now);
                    // He left: an on-site "I'm still here" ends here.
                    if (outsideMs >= graceS * 1000 && isStillHere(j.jobId)) clearStillHere(j.jobId);
                    if (j.state !== "ask" || isStillHere(j.jobId)) continue;
                    if (outsideMs >= graceS * 1000 || resume) {
                        if (!pingedRef.current.has(j.jobId)) {
                            pingedRef.current.add(j.jobId);
                            api.post(`/api/jobs/${j.jobId}/departure-detected`, {
                                distanceM: Math.round(d),
                                lat: pos.coords.latitude,
                                lng: pos.coords.longitude,
                                statusAtDeparture: j.status,
                            }).catch(() => {});
                        }
                        open(j.jobId, resume ? "app_open" : "left_site");
                    }
                }
                forceTick((n) => n + 1);
            },
            () => setGps("off"),
            { enableHighAccuracy: true, maximumAge: 15_000, timeout: 20_000 },
        );
        return () => navigator.geolocation.clearWatch(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [on, watchKey, radiusM, graceS, open]);

    // ── App open / resume ────────────────────────────────────────────────
    useEffect(() => {
        if (!on) return;
        const onVisible = () => {
            if (document.visibilityState !== "visible") return;
            resumeCheckRef.current = true;
            void qc.invalidateQueries({ queryKey: LEFT_OPEN_KEY });
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [on, qc]);

    // With no GPS (or a site with no map pin), opening the app is the trigger.
    useEffect(() => {
        if (!on || prompt) return;
        const first = waiting.find(
            (j) => !isStillHere(j.jobId) && (gps === "off" || j.siteLat == null || j.siteLng == null),
        );
        if (first) open(first.jobId, "app_open");
    }, [on, gps, waiting, prompt, open]);

    // ── Push deep-links: /field/jobs/:id?leaveAnswer=1 ───────────────────
    useEffect(() => {
        if (!on) return;
        const params = new URLSearchParams(location.search);
        if (params.get("leaveAnswer") !== "1") return;
        const m = location.pathname.match(/\/field\/jobs\/([0-9a-f-]{36})/i);
        const jobId = m?.[1];
        params.delete("leaveAnswer");
        navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : "" }, { replace: true });
        if (jobId && byId.has(jobId)) open(jobId, "push");
    }, [on, location.pathname, location.search, byId, navigate, open]);

    // ── The block on On my way ───────────────────────────────────────────
    const guardOnMyWay = useCallback((targetJobId: string, targetLabel?: string | null): boolean => {
        const blocker = blockingJob(liveRef.current, targetJobId, (id) => isStillHere(id));
        if (!blocker) return false;
        setBlock({ jobId: blocker.jobId, nextLabel: targetLabel ?? null });
        return true;
    }, []);

    // ── The answer ───────────────────────────────────────────────────────
    const answer = useMutation({
        mutationFn: async (vars: { jobId: string; body: Omit<LeaveAnswerBody, "tappedAt"> }) => {
            const body: LeaveAnswerBody = { ...vars.body, tappedAt: new Date().toISOString() };
            const url = `/api/jobs/${vars.jobId}/leave-answer`;
            if (!isOnline) {
                await queueOfflineAction({ url, method: "POST", body });
                markAnsweredOffline(vars.jobId);
                setAnsweredRev((n) => n + 1);
                return { queued: true as const, next: null as string | null };
            }
            const res = await api.post<{ next: "done" | "review_submit" }>(url, body);
            return { queued: false as const, next: res.next };
        },
        onSuccess: (res, vars) => {
            const job = byId.get(vars.jobId);
            setPrompt(null);
            setPartsGate(null);
            clearStillHere(vars.jobId);
            void qc.invalidateQueries({ queryKey: LEFT_OPEN_KEY });
            void qc.invalidateQueries({ queryKey: ["field", "jobs"] });
            void qc.invalidateQueries({ queryKey: ["field", "job-detail", vars.jobId] });
            const toReview =
                res.next === "review_submit" ||
                (res.queued && vars.body.choice === "cant_finish" && job && testedCount(job.dots) > 0);
            if (toReview) {
                toast.success("Sent. Now submit what you tested.");
                navigate(`/field/backflow/${vars.jobId}?step=review`);
                return;
            }
            if (res.queued) toast.info("Saved. It'll send when you have signal.");
            else if (vars.body.choice === "cant_finish") toast.success("Sent to the office.");
            else if (vars.body.when === "another_day") toast.success("Sent to scheduling.");
            else toast.success("Got it.");
        },
        onError: (err: unknown, vars) => {
            if (err instanceof ApiError) {
                const body = err.body as { error?: string; parts?: GatePart[]; message?: string } | undefined;
                if (err.status === 409 && body?.error === "PARTS_DISPOSITION_REQUIRED" && Array.isArray(body.parts)) {
                    setPartsGate({ parts: body.parts, body: vars.body });
                    return;
                }
                if (err.status === 409 && body?.error === "JOB_NOT_OPEN") {
                    setPrompt(null);
                    toast.info("The office already has this job.");
                    void qc.invalidateQueries({ queryKey: LEFT_OPEN_KEY });
                    return;
                }
                if (body?.message) {
                    toast.error(body.message);
                    return;
                }
            }
            toast.error("Couldn't send that. Try again.");
        },
    });

    const current = prompt ? byId.get(prompt.jobId) : undefined;
    const blocker = block ? byId.get(block.jobId) : undefined;
    // The blocking job got answered (here, offline, or by the office) — the block lifts.
    useEffect(() => {
        if (block && (!blocker || blocker.state !== "ask")) setBlock(null);
    }, [block, blocker]);
    // The job got answered elsewhere (another tab, the office) — close quietly.
    useEffect(() => {
        if (prompt && !current && !answer.isPending) setPrompt(null);
    }, [prompt, current, answer.isPending]);

    const distance = current ? distanceRef.current.get(current.jobId) : undefined;
    const onSite = gps === "ok" && distance !== undefined ? distance <= radiusM : null;
    const canStayHere = onSite === true || (onSite === null && current ? !isStillHere(current.jobId) : false);
    const blockDistance = blocker ? distanceRef.current.get(blocker.jobId) : undefined;
    const blockOnSite = gps === "ok" && blockDistance !== undefined ? blockDistance <= radiusM : null;
    const blockCanStay =
        blockOnSite === true || (blockOnSite === null && blocker ? !isStillHere(blocker.jobId) : false);

    const value = useMemo<LeftOpenContextValue>(
        () => (on ? { on: true, jobs: live, waiting, openPrompt: open, guardOnMyWay } : OFF),
        [on, live, waiting, open, guardOnMyWay],
    );

    return (
        <LeftOpenContext.Provider value={value}>
            {children}
            {on && blocker && block && !prompt && (
                <LeftOpenBlock
                    job={blocker}
                    nextLabel={block.nextLabel}
                    officePhone={officePhone}
                    canStayHere={blockCanStay}
                    onDone={() => {
                        setBlock(null);
                        navigate(`/field/backflow/${blocker.jobId}?step=review`);
                    }}
                    onComingBack={() =>
                        setPrompt({ jobId: blocker.jobId, trigger: "on_my_way", screen: "coming_back" })
                    }
                    onCantFinish={() =>
                        setPrompt({ jobId: blocker.jobId, trigger: "on_my_way", screen: "cant_finish" })
                    }
                    onStillHere={() => {
                        setStillHere(
                            blocker.jobId,
                            Date.now() + (blockOnSite === true ? STILL_HERE_ON_SITE_MS : STILL_HERE_NO_GPS_MS),
                        );
                        setBlock(null);
                    }}
                    onClose={() => setBlock(null)}
                />
            )}
            {on && current && prompt && (
                <LeftOpenPrompt
                    key={current.jobId}
                    job={current}
                    trigger={prompt.trigger}
                    initialScreen={prompt.screen}
                    onSite={onSite}
                    canStayHere={canStayHere}
                    busy={answer.isPending}
                    onStillHere={() => {
                        setStillHere(
                            current.jobId,
                            Date.now() + (onSite === true ? STILL_HERE_ON_SITE_MS : STILL_HERE_NO_GPS_MS),
                        );
                        setPrompt(null);
                    }}
                    onAllDone={() => {
                        setPrompt(null);
                        navigate(`/field/backflow/${current.jobId}?step=review`);
                    }}
                    onAnswer={(body) => answer.mutate({ jobId: current.jobId, body })}
                />
            )}
            <PartsDispositionDialog
                open={on && partsGate !== null && !!prompt}
                parts={partsGate?.parts ?? []}
                busy={answer.isPending}
                onCancel={() => setPartsGate(null)}
                onSubmit={(answers: PartDispositionAnswer[]) =>
                    prompt &&
                    partsGate &&
                    answer.mutate({ jobId: prompt.jobId, body: { ...partsGate.body, partDispositions: answers } })
                }
            />
        </LeftOpenContext.Provider>
    );
}
